/**
 * Core reservation widget — Widget class + helpers + styles.
 *
 * Two thin entry points consume this module:
 *   - widget.ts        → inline mount via `[data-booking-widget]`
 *   - widget-float.ts  → floating launcher (bottom-right popover)
 *
 * No boot here. Importers decide how to host the Widget.
 */

type ApiService = {
  id: string;
  name: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  depositRequired: boolean;
};

export type ApiConfig = {
  restaurant: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    currency: string;
    locale: string;
    partySize: { min: number; max: number };
    bookingWindowDays: number;
    slotIntervalMinutes: number;
  };
  services: ApiService[];
  // Owner-configurable widget look (settings.php → widget tab). Each field is
  // optional; the widget falls back to "classic" theme + sensible defaults.
  widget?: {
    theme?: string;          // one of THEMES keys
    color?: string | null;   // accent override
    font?: string | null;    // one of FONTS keys (or null = use the theme's default)
    logoUrl?: string | null; // small logo shown in the header
    welcome?: string | null; // welcome message above the date picker
  };
  // Privacy notice under the guest form: the restaurant's policy when set,
  // otherwise RiLoBook's (/confidentialite).
  privacy?: { url?: string | null };
};

type ApiSlot = {
  servicePeriodId: string;
  servicePeriodName: string;
  slotTime: string;
  available: boolean;
};

type ApiAvailability = {
  date: string;
  partySize: number;
  slots: ApiSlot[];
};

type ApiHold = {
  reservationId: string;
  referenceCode: string;
  accessToken: string;
  holdExpiresAt: string;
};

type Step = "pick" | "guest" | "done" | "waitlist";

// ---------- i18n ----------

type Locale = "en" | "it" | "fr" | "nl";
const LOCALES: Locale[] = ["en", "it", "fr", "nl"];

type Messages = {
  loading: string;
  checking: string;
  noTimesAvailable: string;
  joinWaitlist: string;
  change: string;
  back: string;
  dateLabel: string;
  partyLabel: string;
  fullNameLabel: string;
  emailLabel: string;
  phoneLabel: string;
  specialRequestsLabel: string;
  confirmCta: string;
  reservingCta: string;
  addWaitlistCta: string;
  addingWaitlistCta: string;
  nameRequired: string;
  emailOrPhoneRequired: string;
  nameAndContactRequired: string;
  person: string;
  people: string;
  waitlistSummary: string;
  receivedTitle: string;
  waitlistTitle: string;
  receivedSub: string;
  waitlistSub: string;
  reservationsHeader: string;
  fatalLoad: string;
  fatalMissingSlug: string;
  // Floating launcher only.
  floatOpenCta: string;
  floatCloseAria: string;
  // Allergies block (mandatory).
  allergiesQuestion: string;
  allergiesNoneLabel: string;
  allergiesHasLabel: string;
  allergiesPlaceholder: string;
  allergiesRequired: string;
  // Booking comment (free-form message — separate from allergies).
  messageLabel: string;
  messagePlaceholder: string;
  // GDPR notice under the form (restaurant = controller).
  privacyNotice: string;
  privacyLink: string;
  // Explicit consent checkbox for allergies (health data).
  healthConsentLabel: string;
  healthConsentRequired: string;
};

const MESSAGES: Record<Locale, Messages> = {
  en: {
    loading: "Loading…",
    checking: "Checking availability…",
    noTimesAvailable: "No times available.",
    joinWaitlist: "Join the waitlist",
    change: "← Change",
    back: "← Back",
    dateLabel: "Date",
    partyLabel: "Party",
    fullNameLabel: "Full name",
    emailLabel: "Email",
    phoneLabel: "Phone",
    specialRequestsLabel: "Special requests (optional)",
    confirmCta: "Confirm reservation",
    reservingCta: "Reserving…",
    addWaitlistCta: "Add me to the waitlist",
    addingWaitlistCta: "Adding…",
    nameRequired: "Full name is required",
    emailOrPhoneRequired: "Email or phone is required",
    nameAndContactRequired: "Name and email or phone are required",
    person: "person",
    people: "people",
    waitlistSummary: "Waitlist · {date} · party of {n}",
    receivedTitle: "Reservation received",
    waitlistTitle: "You're on the waitlist",
    receivedSub: "Reference {ref}. We'll email you to confirm.",
    waitlistSub: "We'll reach out if a table opens up.",
    reservationsHeader: "Reservations",
    fatalLoad: "Could not load widget: {error}",
    fatalMissingSlug: "Missing data-restaurant attribute",
    floatOpenCta: "Book a table",
    floatCloseAria: "Close",
    allergiesQuestion: "Allergies & dietary requirements (optional)",
    allergiesNoneLabel: "No allergies",
    allergiesHasLabel: "I have allergies / dietary needs",
    allergiesPlaceholder: "List allergies or dietary needs (e.g. gluten, nuts, lactose, vegetarian…)",
    allergiesRequired: "Please confirm no allergies or list them.",
    messageLabel: "Message to the restaurant (optional)",
    messagePlaceholder: "Birthday, special occasion, accessibility need, etc.",
    privacyNotice: "Your details are passed to {restaurant} to manage your reservation. Any allergies you mention are erased 30 days after your visit.",
    privacyLink: "Privacy",
    healthConsentLabel: "I agree that {restaurant} may use this health information (allergies) to prepare my visit. It is erased 30 days later.",
    healthConsentRequired: "To share allergies, please tick the consent box (or remove them).",
  },
  it: {
    loading: "Caricamento…",
    checking: "Verifica disponibilità…",
    noTimesAvailable: "Nessun orario disponibile.",
    joinWaitlist: "Iscriviti alla lista d'attesa",
    change: "← Cambia",
    back: "← Indietro",
    dateLabel: "Data",
    partyLabel: "Coperti",
    fullNameLabel: "Nome completo",
    emailLabel: "Email",
    phoneLabel: "Telefono",
    specialRequestsLabel: "Richieste particolari (facoltativo)",
    confirmCta: "Conferma prenotazione",
    reservingCta: "Prenotazione…",
    addWaitlistCta: "Iscrivimi alla lista d'attesa",
    addingWaitlistCta: "Aggiunta…",
    nameRequired: "Il nome è obbligatorio",
    emailOrPhoneRequired: "Email o telefono sono obbligatori",
    nameAndContactRequired: "Nome ed email o telefono sono obbligatori",
    person: "persona",
    people: "persone",
    waitlistSummary: "Lista d'attesa · {date} · {n} coperti",
    receivedTitle: "Prenotazione ricevuta",
    waitlistTitle: "Sei in lista d'attesa",
    receivedSub: "Riferimento {ref}. Ti invieremo un'email di conferma.",
    waitlistSub: "Ti contatteremo se si libera un tavolo.",
    reservationsHeader: "Prenotazioni",
    fatalLoad: "Impossibile caricare il widget: {error}",
    fatalMissingSlug: "Attributo data-restaurant mancante",
    floatOpenCta: "Prenota un tavolo",
    floatCloseAria: "Chiudi",
    allergiesQuestion: "Allergie e regimi alimentari (facoltativo)",
    allergiesNoneLabel: "Nessuna allergia",
    allergiesHasLabel: "Ho allergie / esigenze alimentari",
    allergiesPlaceholder: "Indica le allergie o esigenze (es. glutine, noci, lattosio, vegetariano…)",
    allergiesRequired: "Conferma nessuna allergia oppure indicale.",
    messageLabel: "Messaggio per il ristorante (facoltativo)",
    messagePlaceholder: "Compleanno, occasione speciale, esigenza di accessibilità, ecc.",
    privacyNotice: "I tuoi dati sono trasmessi a {restaurant} per gestire la tua prenotazione. Le eventuali allergie indicate vengono cancellate 30 giorni dopo la visita.",
    privacyLink: "Privacy",
    healthConsentLabel: "Accetto che {restaurant} usi questa informazione sulla mia salute (allergie) per preparare la mia visita. Viene cancellata 30 giorni dopo.",
    healthConsentRequired: "Per indicare allergie, spunta la casella di consenso (oppure cancellale).",
  },
  fr: {
    loading: "Chargement…",
    checking: "Vérification de la disponibilité…",
    noTimesAvailable: "Aucun créneau disponible.",
    joinWaitlist: "Rejoindre la liste d'attente",
    change: "← Modifier",
    back: "← Retour",
    dateLabel: "Date",
    partyLabel: "Couverts",
    fullNameLabel: "Nom complet",
    emailLabel: "Email",
    phoneLabel: "Téléphone",
    specialRequestsLabel: "Demandes particulières (optionnel)",
    confirmCta: "Confirmer la réservation",
    reservingCta: "Réservation…",
    addWaitlistCta: "M'ajouter à la liste d'attente",
    addingWaitlistCta: "Ajout…",
    nameRequired: "Le nom est obligatoire",
    emailOrPhoneRequired: "Email ou téléphone obligatoire",
    nameAndContactRequired: "Nom et email ou téléphone obligatoires",
    person: "personne",
    people: "personnes",
    waitlistSummary: "Liste d'attente · {date} · {n} couverts",
    receivedTitle: "Réservation reçue",
    waitlistTitle: "Vous êtes en liste d'attente",
    receivedSub: "Référence {ref}. Nous vous enverrons un email de confirmation.",
    waitlistSub: "Nous vous recontacterons si une table se libère.",
    reservationsHeader: "Réservations",
    fatalLoad: "Impossible de charger le widget : {error}",
    fatalMissingSlug: "Attribut data-restaurant manquant",
    floatOpenCta: "Réserver une table",
    floatCloseAria: "Fermer",
    allergiesQuestion: "Allergies & régimes alimentaires (optionnel)",
    allergiesNoneLabel: "Aucune allergie",
    allergiesHasLabel: "J'ai des allergies / un régime",
    allergiesPlaceholder: "Indiquez les allergies ou régimes (ex : gluten, noix, lactose, végétarien…)",
    allergiesRequired: "Veuillez confirmer aucune allergie ou les indiquer.",
    messageLabel: "Message au restaurant (optionnel)",
    messagePlaceholder: "Anniversaire, occasion spéciale, besoin d'accessibilité, etc.",
    privacyNotice: "Vos données sont transmises à {restaurant} pour gérer votre réservation. Les allergies éventuelles sont effacées 30 jours après votre venue.",
    privacyLink: "Confidentialité",
    healthConsentLabel: "J'accepte que {restaurant} utilise cette information sur ma santé (allergies) pour préparer ma venue. Elle est effacée 30 jours après.",
    healthConsentRequired: "Pour transmettre des allergies, cochez la case de consentement (ou effacez-les).",
  },
  nl: {
    loading: "Laden…",
    checking: "Beschikbaarheid controleren…",
    noTimesAvailable: "Geen tijdstippen beschikbaar.",
    joinWaitlist: "Op de wachtlijst",
    change: "← Wijzigen",
    back: "← Terug",
    dateLabel: "Datum",
    partyLabel: "Personen",
    fullNameLabel: "Volledige naam",
    emailLabel: "E-mail",
    phoneLabel: "Telefoon",
    specialRequestsLabel: "Bijzondere wensen (optioneel)",
    confirmCta: "Reservatie bevestigen",
    reservingCta: "Reserveren…",
    addWaitlistCta: "Zet me op de wachtlijst",
    addingWaitlistCta: "Toevoegen…",
    nameRequired: "Naam is verplicht",
    emailOrPhoneRequired: "E-mail of telefoon is verplicht",
    nameAndContactRequired: "Naam en e-mail of telefoon zijn verplicht",
    person: "persoon",
    people: "personen",
    waitlistSummary: "Wachtlijst · {date} · {n} personen",
    receivedTitle: "Reservatie ontvangen",
    waitlistTitle: "U staat op de wachtlijst",
    receivedSub: "Referentie {ref}. U ontvangt een bevestiging per e-mail.",
    waitlistSub: "We nemen contact op als er een tafel vrijkomt.",
    reservationsHeader: "Reservaties",
    fatalLoad: "Widget kon niet worden geladen: {error}",
    fatalMissingSlug: "Attribuut data-restaurant ontbreekt",
    floatOpenCta: "Reserveer een tafel",
    floatCloseAria: "Sluiten",
    allergiesQuestion: "Allergieën en dieetwensen (optioneel)",
    allergiesNoneLabel: "Geen allergieën",
    allergiesHasLabel: "Ik heb allergieën / dieetwensen",
    allergiesPlaceholder: "Vermeld allergieën of dieetwensen (bv. gluten, noten, lactose, vegetarisch…)",
    allergiesRequired: "Bevestig dat u geen allergieën hebt of vermeld ze.",
    messageLabel: "Bericht aan het restaurant (optioneel)",
    messagePlaceholder: "Verjaardag, speciale gelegenheid, toegankelijkheid, enz.",
    privacyNotice: "Uw gegevens worden doorgegeven aan {restaurant} om uw reservatie te beheren. Eventuele allergieën worden 30 dagen na uw bezoek gewist.",
    privacyLink: "Privacy",
    healthConsentLabel: "Ik ga ermee akkoord dat {restaurant} deze gezondheidsinformatie (allergieën) gebruikt om mijn bezoek voor te bereiden. Ze wordt 30 dagen later gewist.",
    healthConsentRequired: "Vink het toestemmingsvakje aan om allergieën door te geven (of wis ze).",
  },
};

export function resolveLocale(hostAttr: string | null, configLocale: string | undefined): Locale {
  const candidates = [
    hostAttr,
    configLocale,
    typeof navigator !== "undefined" ? navigator.language : null,
  ]
    .filter((x): x is string => typeof x === "string")
    .map((s) => s.toLowerCase().split("-")[0]);
  for (const c of candidates) {
    if ((LOCALES as string[]).includes(c as string)) return c as Locale;
  }
  return "en";
}

export function messagesFor(locale: Locale): Messages {
  return MESSAGES[locale];
}

function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));
}

interface WidgetState {
  date: string;
  partySize: number;
  chosen: ApiSlot | null;
  hold: ApiHold | null;
  step: Step;
  loadingAvail: boolean;
  submitting: boolean;
  error: string | null;
  config: ApiConfig | null;
  slots: ApiSlot[];
  guest: {
    fullName: string;
    email: string;
    phone: string;
    specialRequests: string;
    /** Optional: "none" = "no allergies"; "has" = the textarea below is in use. */
    allergyMode: "" | "none" | "has";
    allergiesText: string;
    /** Explicit consent (GDPR art. 9.2.a) — unticked by default, required if allergies are typed. */
    healthConsent: boolean;
  };
}

// ---------- Fonts ----------
// Curated font-family stacks. We deliberately avoid network-loaded fonts so
// the widget stays embeddable on any host without triggering CSP errors or
// FOIT/FOUT on first paint. Owners pick a key; the widget maps it to a stack.

export type FontName =
  | "system" | "serif" | "modern" | "elegant" | "monospace"
  | "rounded" | "playful" | "compact" | "condensed" | "humanist";

export const FONT_STACKS: Record<FontName, string> = {
  // Native UI font (matches macOS / iOS / Windows) — best default.
  system:    'system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif',
  // Editorial / refined — books, restaurants with a heritage feel.
  serif:     '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
  // Geometric sans — clean, fintech-y. Falls back gracefully when Inter is absent.
  modern:    'Inter, "Helvetica Neue", -apple-system, "Segoe UI", system-ui, sans-serif',
  // Display serif — high-contrast, "fine dining" register.
  elegant:   '"Playfair Display", "Hoefler Text", "Didot", Garamond, serif',
  // Monospaced — terminal/retro look. Fixed-width digits help slot grids align.
  monospace: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Consolas, monospace',
  // Friendly rounded — pastel/family-restaurant vibes.
  rounded:   '"Avenir Next", "Nunito", "Trebuchet MS", "Segoe UI", sans-serif',
  // Old-school book serif — cozy, breakfast café energy.
  playful:   '"Bookman Old Style", Bookman, "URW Bookman L", Georgia, serif',
  // Dense Helvetica — packs more info, professional/Swiss feel.
  compact:   '"Helvetica Neue", Helvetica, Arial, "Liberation Sans", sans-serif',
  // Narrow / condensed — for restaurants with long names that wrap awkwardly.
  condensed: '"Roboto Condensed", "Arial Narrow", "Helvetica Condensed", sans-serif',
  // Humanist sans — warmer than geometric, less rigid than system-ui.
  humanist:  'Verdana, "Lucida Grande", "Bitstream Vera Sans", "DejaVu Sans", sans-serif',
};

function fontStack(name: string | null | undefined): string | undefined {
  if (!name) return undefined;
  return (FONT_STACKS as Record<string, string>)[name];
}

// ---------- Themes ----------
// 20 baked-in palettes. The widget reads `data-theme` (declared on the host
// or `widget.theme` from the owner's settings) and applies the corresponding
// CSS variables onto the shadow root. A custom accent / radius / font can
// still override any individual token via data-color / data-radius / data-font.

export type ThemeName =
  // Phase 1 — first 10 baked-in palettes.
  | "classic" | "midnight" | "warm" | "fresh" | "minimal"
  | "elegant" | "vibrant" | "cozy" | "modern" | "retro"
  // Phase 2 — 10 more palettes to give owners real choice.
  | "bistro" | "coastal" | "forest" | "terracotta" | "noir"
  | "pastel" | "industrial" | "jardin" | "artDeco" | "bauhaus";

interface ThemeTokens {
  bg: string; surface: string; text: string; muted: string;
  border: string; accent: string; accentText: string; radius: string;
  font: string;
}

export const THEMES: Record<ThemeName, ThemeTokens> = {
  // ── Phase 1 ──────────────────────────────────────────────────────────
  classic:  { bg: "#fff",     surface: "#fff",    text: "#111",    muted: "#666",    border: "#e5e7eb", accent: "#111111", accentText: "#fff", radius: "12px", font: FONT_STACKS.system },
  midnight: { bg: "#0f172a", surface: "#1e293b", text: "#f1f5f9", muted: "#94a3b8", border: "#334155", accent: "#38bdf8", accentText: "#0f172a", radius: "12px", font: FONT_STACKS.system },
  warm:     { bg: "#fdf6ec", surface: "#fffaf0", text: "#3d2914", muted: "#8a6d4f", border: "#e8d8c4", accent: "#c8553d", accentText: "#fff", radius: "14px", font: FONT_STACKS.serif },
  fresh:    { bg: "#f0fdf4", surface: "#fff",    text: "#14532d", muted: "#5d8068", border: "#bbf7d0", accent: "#16a34a", accentText: "#fff", radius: "10px", font: FONT_STACKS.system },
  minimal:  { bg: "#fafafa", surface: "#fff",    text: "#171717", muted: "#737373", border: "#e5e5e5", accent: "#000",    accentText: "#fff", radius: "4px",  font: FONT_STACKS.modern },
  elegant:  { bg: "#1c1917", surface: "#292524", text: "#fafaf9", muted: "#a8a29e", border: "#44403c", accent: "#d4af37", accentText: "#1c1917", radius: "2px",  font: FONT_STACKS.elegant },
  vibrant:  { bg: "#fff",    surface: "#fff",    text: "#1f2937", muted: "#6b7280", border: "#e5e7eb", accent: "#7c3aed", accentText: "#fff", radius: "16px", font: FONT_STACKS.system },
  cozy:     { bg: "#fef3c7", surface: "#fffbeb", text: "#451a03", muted: "#92400e", border: "#fcd34d", accent: "#b45309", accentText: "#fff", radius: "20px", font: FONT_STACKS.playful },
  modern:   { bg: "#f9fafb", surface: "#fff",    text: "#111827", muted: "#6b7280", border: "#d1d5db", accent: "#2563eb", accentText: "#fff", radius: "8px",  font: FONT_STACKS.modern },
  retro:    { bg: "#fef2f2", surface: "#fff",    text: "#7c1d1d", muted: "#9f1239", border: "#fecdd3", accent: "#e11d48", accentText: "#fff", radius: "0px",  font: FONT_STACKS.monospace },
  // ── Phase 2 ──────────────────────────────────────────────────────────
  bistro:    { bg: "#fbf6f1", surface: "#fffdfa", text: "#3d1f15", muted: "#8a5040", border: "#dcc0ac", accent: "#8b1e2c", accentText: "#fff",    radius: "10px", font: FONT_STACKS.serif },
  coastal:   { bg: "#f0f8fc", surface: "#fff",    text: "#0c3d5e", muted: "#5b7a8d", border: "#bcd7e6", accent: "#0277bd", accentText: "#fff",    radius: "10px", font: FONT_STACKS.system },
  forest:    { bg: "#f4f6f0", surface: "#fff",    text: "#1f3a25", muted: "#6b7a64", border: "#c4d2b8", accent: "#2d5a3a", accentText: "#fff",    radius: "8px",  font: FONT_STACKS.serif },
  terracotta:{ bg: "#fbf2eb", surface: "#fff",    text: "#4a2415", muted: "#a16953", border: "#e6c9b5", accent: "#d2691e", accentText: "#fff",    radius: "14px", font: FONT_STACKS.serif },
  noir:      { bg: "#000",    surface: "#0a0a0a", text: "#f5f5f5", muted: "#888",    border: "#222",    accent: "#facc15", accentText: "#000",    radius: "0px",  font: FONT_STACKS.modern },
  pastel:    { bg: "#fdf4f7", surface: "#fff",    text: "#4a1a3a", muted: "#9c6b8a", border: "#f4d4e0", accent: "#db6db5", accentText: "#fff",    radius: "20px", font: FONT_STACKS.rounded },
  industrial:{ bg: "#1a1d22", surface: "#252a30", text: "#e8e9ea", muted: "#8a8e94", border: "#3a3f47", accent: "#ff6b35", accentText: "#000",    radius: "2px",  font: FONT_STACKS.compact },
  jardin:    { bg: "#f8faf5", surface: "#fff",    text: "#2d3e1f", muted: "#7a8a6b", border: "#d4e0c4", accent: "#7cb342", accentText: "#fff",    radius: "16px", font: FONT_STACKS.serif },
  artDeco:   { bg: "#1c1410", surface: "#2a1f17", text: "#f5e9d5", muted: "#b09678", border: "#5c4a3a", accent: "#d4af37", accentText: "#1c1410", radius: "0px",  font: FONT_STACKS.elegant },
  bauhaus:   { bg: "#fff",    surface: "#fff",    text: "#000",    muted: "#666",    border: "#000",    accent: "#e63946", accentText: "#fff",    radius: "0px",  font: FONT_STACKS.modern },
};

function themeTokens(name: string | undefined | null): ThemeTokens {
  if (name && (name in THEMES)) return THEMES[name as ThemeName];
  return THEMES.classic;
}

function buildThemeCss(t: ThemeTokens, override?: { accent?: string; radius?: string; font?: string }): string {
  const acc = override?.accent || t.accent;
  const rad = override?.radius || t.radius;
  // Font override takes precedence: when the owner picks "modern" font on the
  // "warm" theme we want modern to win. Empty/invalid values fall back to the
  // theme's intrinsic font (so an empty data-font attribute is a no-op).
  const fnt = override?.font && override.font.trim() ? override.font : t.font;
  return `
:host { all: initial; }
.bw-card {
  --bw-bg: ${t.bg};
  --bw-surface: ${t.surface};
  --bw-text: ${t.text};
  --bw-muted: ${t.muted};
  --bw-border: ${t.border};
  --bw-accent: ${acc};
  --bw-accent-text: ${t.accentText};
  --bw-radius: ${rad};
  font-family: ${fnt};
  color: var(--bw-text);
  background: var(--bw-surface);
  border: 1px solid var(--bw-border);
  border-radius: var(--bw-radius);
  padding: 1rem;
  max-width: 420px;
  display: block;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}`;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const SCRIPT_EL =
  typeof document !== "undefined" ? document.currentScript ?? null : null;
export const DEFAULT_API_BASE = (() => {
  if (SCRIPT_EL && "src" in SCRIPT_EL && SCRIPT_EL.src) {
    try {
      return new URL((SCRIPT_EL as HTMLScriptElement).src).origin;
    } catch {
      /* ignore */
    }
  }
  return typeof window !== "undefined" ? window.location.origin : "";
})();

export class Widget {
  private root: ShadowRoot;
  private styleEl: HTMLStyleElement;
  private slug: string;
  private apiBase: string;
  private state: WidgetState;
  private mountNode: HTMLElement;
  private localeAttr: string | null;
  private referralCode: string | null;
  private themeAttr: string | null;
  private colorAttr: string | null;
  private fontAttr: string | null;
  private logoAttr: string | null;
  private welcomeAttr: string | null;
  private m: Messages;

  constructor(host: HTMLElement) {
    this.slug = host.getAttribute("data-restaurant") ?? "";
    this.apiBase = host.getAttribute("data-api-base") ?? DEFAULT_API_BASE;
    this.localeAttr = host.getAttribute("data-locale");
    this.themeAttr = host.getAttribute("data-theme");
    this.colorAttr = host.getAttribute("data-color");
    this.fontAttr = host.getAttribute("data-font");
    this.logoAttr = host.getAttribute("data-logo");
    this.welcomeAttr = host.getAttribute("data-welcome");
    this.referralCode =
      host.getAttribute("data-referral") ??
      (typeof window !== "undefined"
        ? new URL(window.location.href).searchParams.get("ref")
        : null);

    this.root = host.attachShadow({ mode: "open" });
    this.mountNode = document.createElement("div");
    this.styleEl = document.createElement("style");
    this.styleEl.textContent = buildThemeCss(themeTokens(this.themeAttr), {
      accent: this.colorAttr || undefined,
      font: fontStack(this.fontAttr),
    }) + LAYOUT_CSS;
    this.root.appendChild(this.styleEl);
    this.root.appendChild(this.mountNode);

    this.m = MESSAGES[resolveLocale(this.localeAttr, undefined)];

    this.state = {
      date: todayISO(),
      partySize: 2,
      chosen: null,
      hold: null,
      step: "pick",
      loadingAvail: false,
      submitting: false,
      error: null,
      config: null,
      slots: [],
      guest: { fullName: "", email: "", phone: "", specialRequests: "", allergyMode: "", allergiesText: "", healthConsent: false },
    };

    void this.init();
  }

  /**
   * Re-apply the theme — used by the live preview in /dashboard/settings so the
   * owner can flip themes/fonts without remounting. Each call rebuilds the
   * style block; passing null for a field keeps the previously-set override.
   */
  setTheme(theme: string | null, color?: string | null, font?: string | null): void {
    this.themeAttr = theme;
    this.colorAttr = color ?? this.colorAttr;
    this.fontAttr = font ?? this.fontAttr;
    this.styleEl.textContent = buildThemeCss(themeTokens(this.themeAttr), {
      accent: this.colorAttr || undefined,
      font: fontStack(this.fontAttr),
    }) + LAYOUT_CSS;
  }

  private async init(): Promise<void> {
    if (!this.slug) {
      this.renderFatal(this.m.fatalMissingSlug);
      return;
    }
    try {
      const res = await fetch(`${this.apiBase}/api/public/widget-config?slug=${encodeURIComponent(this.slug)}`);
      if (!res.ok) throw new Error(`config ${res.status}`);
      this.state.config = (await res.json()) as ApiConfig;
      this.m = MESSAGES[resolveLocale(this.localeAttr, this.state.config.restaurant.locale)];
      // Owner-configured widget look — only apply if a data-attribute did NOT
      // already pin a specific theme/color. The host attribute always wins.
      const w = this.state.config.widget;
      if (w) {
        if (!this.themeAttr && w.theme) this.themeAttr = w.theme;
        if (!this.colorAttr && w.color) this.colorAttr = w.color;
        if (!this.fontAttr && w.font) this.fontAttr = w.font;
        if (!this.logoAttr && w.logoUrl) this.logoAttr = w.logoUrl;
        if (!this.welcomeAttr && w.welcome) this.welcomeAttr = w.welcome;
        if (this.themeAttr || this.colorAttr || this.fontAttr) {
          this.styleEl.textContent = buildThemeCss(themeTokens(this.themeAttr), {
            accent: this.colorAttr || undefined,
            font: fontStack(this.fontAttr),
          }) + LAYOUT_CSS;
        }
      }
      this.render();
      void this.loadAvailability();
    } catch (err) {
      this.renderFatal(interpolate(this.m.fatalLoad, { error: (err as Error).message }));
    }
  }

  private async loadAvailability(): Promise<void> {
    if (!this.state.config) return;
    this.state.loadingAvail = true;
    this.state.error = null;
    this.state.slots = [];
    this.render();
    try {
      const url = new URL(`${this.apiBase}/api/public/availability`);
      url.searchParams.set("slug", this.slug);
      url.searchParams.set("date", this.state.date);
      url.searchParams.set("partySize", String(this.state.partySize));
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`availability ${res.status}`);
      const data = (await res.json()) as ApiAvailability;
      this.state.slots = data.slots;
    } catch (err) {
      this.state.error = (err as Error).message;
    } finally {
      this.state.loadingAvail = false;
      this.render();
    }
  }

  private async submitHold(): Promise<void> {
    if (!this.state.chosen || this.state.submitting) return;
    this.state.submitting = true;
    this.state.error = null;
    this.render();
    try {
      // Allergies travel in their own field with the explicit consent flag; the
      // server checks the consent, then prefixes them into the booking notes.
      const allergies = this.state.guest.allergyMode === "has" ? this.state.guest.allergiesText.trim() : "";

      const body = {
        slug: this.slug,
        servicePeriodId: this.state.chosen.servicePeriodId,
        serviceDate: this.state.date,
        slotTime: this.state.chosen.slotTime,
        partySize: this.state.partySize,
        guest: {
          fullName: this.state.guest.fullName,
          email: this.state.guest.email || undefined,
          phone: this.state.guest.phone || undefined,
          specialRequests: this.state.guest.specialRequests.trim() || undefined,
          allergies: allergies || undefined,
          healthConsent: allergies ? this.state.guest.healthConsent : undefined,
        },
        referralCode: this.referralCode || undefined,
      };
      const res = await fetch(`${this.apiBase}/api/public/holds`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        if (payload.code === "health_consent_required") throw new Error(this.m.healthConsentRequired);
        throw new Error(payload.code ?? `hold ${res.status}`);
      }
      const hold = (await res.json()) as ApiHold;
      this.state.hold = hold;

      const service = this.state.config?.services.find(
        (s) => s.id === this.state.chosen!.servicePeriodId,
      );
      if (service?.depositRequired) {
        const csRes = await fetch(`${this.apiBase}/api/public/checkout/create-session`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            reservationId: hold.reservationId,
            accessToken: hold.accessToken,
          }),
        });
        if (!csRes.ok) {
          throw new Error(`checkout ${csRes.status}`);
        }
        const { checkoutUrl } = (await csRes.json()) as { checkoutUrl: string };
        window.location.href = checkoutUrl;
        return;
      }

      this.state.step = "done";
    } catch (err) {
      this.state.error = (err as Error).message;
    } finally {
      this.state.submitting = false;
      this.render();
    }
  }

  private async submitWaitlist(): Promise<void> {
    if (!this.state.config || this.state.submitting) return;
    this.state.submitting = true;
    this.state.error = null;
    this.render();
    try {
      const periodId =
        this.state.slots[0]?.servicePeriodId ?? this.state.config.services[0]?.id;
      if (!periodId) throw new Error("no_services_available");

      const res = await fetch(`${this.apiBase}/api/public/waitlist`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          slug: this.slug,
          servicePeriodId: periodId,
          serviceDate: this.state.date,
          partySize: this.state.partySize,
          guest: {
            fullName: this.state.guest.fullName,
            email: this.state.guest.email || undefined,
            phone: this.state.guest.phone || undefined,
          },
        }),
      });
      if (!res.ok) throw new Error(`waitlist ${res.status}`);
      this.state.step = "done";
    } catch (err) {
      this.state.error = (err as Error).message;
    } finally {
      this.state.submitting = false;
      this.render();
    }
  }

  private renderFatal(msg: string): void {
    this.mountNode.replaceChildren(el("div", { className: "bw-fatal" }, [text(msg)]));
  }

  private render(): void {
    if (!this.state.config) {
      this.mountNode.replaceChildren(el("div", { className: "bw-loading" }, [text(this.m.loading)]));
      return;
    }
    const headerChildren: Node[] = [];
    if (this.logoAttr) {
      const img = el("img", { src: this.logoAttr, alt: "", className: "bw-logo" });
      headerChildren.push(img);
    }
    headerChildren.push(el("strong", {}, [text(this.state.config.restaurant.name)]));
    headerChildren.push(el("span", { className: "bw-sub" }, [text(this.m.reservationsHeader)]));
    if (this.welcomeAttr) {
      headerChildren.push(el("p", { className: "bw-welcome" }, [text(this.welcomeAttr)]));
    }

    const card = el("div", { className: "bw-card" }, [
      el("header", { className: "bw-header" }, headerChildren),
      this.renderBody(),
    ]);
    this.mountNode.replaceChildren(card);
  }

  private renderBody(): HTMLElement {
    const s = this.state;
    if (s.step === "done") return this.renderDone();
    if (s.step === "waitlist") return this.renderWaitlist();
    if (s.step === "guest") return this.renderGuest();
    return this.renderPick();
  }

  private renderPick(): HTMLElement {
    const s = this.state;
    const cfg = s.config!.restaurant;

    const dateInput = el("input", {
      type: "date",
      value: s.date,
      min: todayISO(),
      className: "bw-input",
    });
    dateInput.addEventListener("change", () => {
      s.date = (dateInput as HTMLInputElement).value;
      void this.loadAvailability();
    });

    const partySelect = el("select", { className: "bw-input" });
    for (let i = cfg.partySize.min; i <= cfg.partySize.max; i += 1) {
      const opt = el("option", { value: String(i) }, [
        text(`${i} ${i === 1 ? this.m.person : this.m.people}`),
      ]);
      if (i === s.partySize) (opt as HTMLOptionElement).selected = true;
      partySelect.appendChild(opt);
    }
    partySelect.addEventListener("change", () => {
      s.partySize = Number((partySelect as HTMLSelectElement).value);
      void this.loadAvailability();
    });

    const available = s.slots.filter((sl) => sl.available);
    const grouped = new Map<string, ApiSlot[]>();
    for (const slot of available) {
      const list = grouped.get(slot.servicePeriodName) ?? [];
      list.push(slot);
      grouped.set(slot.servicePeriodName, list);
    }

    const slotSection = el("div", { className: "bw-slots" });
    if (s.loadingAvail) {
      slotSection.appendChild(el("div", { className: "bw-muted" }, [text(this.m.checking)]));
    } else if (available.length === 0) {
      slotSection.appendChild(el("div", { className: "bw-muted" }, [text(this.m.noTimesAvailable)]));
      const waitBtn = el("button", { className: "bw-btn bw-btn-ghost" }, [text(this.m.joinWaitlist)]);
      waitBtn.addEventListener("click", () => {
        s.step = "waitlist";
        this.render();
      });
      slotSection.appendChild(waitBtn);
    } else {
      for (const [periodName, slots] of grouped) {
        slotSection.appendChild(el("div", { className: "bw-period" }, [text(periodName)]));
        const group = el("div", { className: "bw-slot-grid" });
        for (const slot of slots) {
          const btn = el("button", { className: "bw-slot" }, [text(slot.slotTime.slice(0, 5))]);
          btn.addEventListener("click", () => {
            s.chosen = slot;
            s.step = "guest";
            this.render();
          });
          group.appendChild(btn);
        }
        slotSection.appendChild(group);
      }
    }

    return el("section", { className: "bw-body" }, [
      el("div", { className: "bw-row" }, [
        labelled(this.m.dateLabel, dateInput),
        labelled(this.m.partyLabel, partySelect),
      ]),
      s.error ? el("div", { className: "bw-error" }, [text(s.error)]) : el("span", {}, []),
      slotSection,
    ]);
  }

  private renderGuest(): HTMLElement {
    const s = this.state;
    const chosen = s.chosen!;

    const nameInput = textInput(this.m.fullNameLabel, s.guest.fullName, (v) => (s.guest.fullName = v));
    const emailInput = textInput(this.m.emailLabel, s.guest.email, (v) => (s.guest.email = v), "email");
    const phoneInput = textInput(this.m.phoneLabel, s.guest.phone, (v) => (s.guest.phone = v), "tel");
    const messageInput = textareaInput(this.m.messageLabel, this.m.messagePlaceholder, s.guest.specialRequests, (v) => (s.guest.specialRequests = v));

    // Allergies block — radio + conditional textarea. Both inline so it
    // visually reads as one decision the guest must make.
    const allergyBlock = el("div", { className: "bw-allergy-block" });
    const allergyTitle = el("div", { className: "bw-allergy-title" }, [text(this.m.allergiesQuestion)]);
    allergyBlock.appendChild(allergyTitle);

    const radioRow = el("div", { className: "bw-allergy-radios" });
    const optNone = el("label", { className: "bw-radio" }, []);
    const noneInput = el("input", { type: "radio", name: "bw-allergy" }) as HTMLInputElement;
    if (s.guest.allergyMode === "none") noneInput.checked = true;
    noneInput.addEventListener("change", () => { s.guest.allergyMode = "none"; s.error = null; this.render(); });
    optNone.appendChild(noneInput);
    optNone.appendChild(text(this.m.allergiesNoneLabel));

    const optHas = el("label", { className: "bw-radio" }, []);
    const hasInput = el("input", { type: "radio", name: "bw-allergy" }) as HTMLInputElement;
    if (s.guest.allergyMode === "has") hasInput.checked = true;
    hasInput.addEventListener("change", () => { s.guest.allergyMode = "has"; s.error = null; this.render(); });
    optHas.appendChild(hasInput);
    optHas.appendChild(text(this.m.allergiesHasLabel));

    radioRow.appendChild(optNone);
    radioRow.appendChild(optHas);
    allergyBlock.appendChild(radioRow);

    if (s.guest.allergyMode === "has") {
      const ta = el("textarea", {
        className: "bw-input bw-textarea",
        placeholder: this.m.allergiesPlaceholder,
        rows: 2,
      }) as HTMLTextAreaElement;
      ta.value = s.guest.allergiesText;
      ta.addEventListener("input", () => { s.guest.allergiesText = ta.value; });
      allergyBlock.appendChild(ta);

      // Explicit consent for health data — never pre-ticked.
      const consentInput = el("input", { type: "checkbox" }) as HTMLInputElement;
      consentInput.checked = s.guest.healthConsent;
      consentInput.addEventListener("change", () => { s.guest.healthConsent = consentInput.checked; s.error = null; });
      const consentLabel = el("label", { className: "bw-radio bw-consent" }, [
        consentInput,
        text(interpolate(this.m.healthConsentLabel, { restaurant: this.state.config?.restaurant.name ?? "" })),
      ]);
      allergyBlock.appendChild(consentLabel);
    }

    const submit = el("button", { className: "bw-btn" }, [
      text(s.submitting ? this.m.reservingCta : this.m.confirmCta),
    ]);
    (submit as HTMLButtonElement).disabled = s.submitting;
    submit.addEventListener("click", () => {
      if (!s.guest.fullName.trim()) {
        s.error = this.m.nameRequired;
        this.render();
        return;
      }
      if (!s.guest.email && !s.guest.phone) {
        s.error = this.m.emailOrPhoneRequired;
        this.render();
        return;
      }
      // Allergies are optional; if any are typed, explicit consent is required.
      if (s.guest.allergyMode === "has" && s.guest.allergiesText.trim() && !s.guest.healthConsent) {
        s.error = this.m.healthConsentRequired;
        this.render();
        return;
      }
      void this.submitHold();
    });

    const back = el("button", { className: "bw-btn bw-btn-ghost" }, [text(this.m.change)]);
    back.addEventListener("click", () => {
      s.step = "pick";
      s.chosen = null;
      s.error = null;
      this.render();
    });

    return el("section", { className: "bw-body" }, [
      el("div", { className: "bw-summary" }, [
        text(`${s.date} · ${chosen.slotTime.slice(0, 5)} · ${s.partySize} · ${chosen.servicePeriodName}`),
      ]),
      nameInput,
      emailInput,
      phoneInput,
      allergyBlock,
      messageInput,
      s.error ? el("div", { className: "bw-error" }, [text(s.error)]) : el("span", {}, []),
      el("div", { className: "bw-actions" }, [back, submit]),
      this.privacyNotice(),
    ]);
  }

  /** Short GDPR notice: who receives the data, with a link to the policy. */
  private privacyNotice(): HTMLElement {
    const cfg = this.state.config;
    const url = cfg?.privacy?.url || `${this.apiBase}/confidentialite`;
    const link = el("a", { href: url, target: "_blank", rel: "noopener" }, [text(this.m.privacyLink)]);
    return el("p", { className: "bw-privacy" }, [
      text(interpolate(this.m.privacyNotice, { restaurant: cfg?.restaurant.name ?? "" }) + " "),
      link,
    ]);
  }

  private renderWaitlist(): HTMLElement {
    const s = this.state;
    const nameInput = textInput(this.m.fullNameLabel, s.guest.fullName, (v) => (s.guest.fullName = v));
    const emailInput = textInput(this.m.emailLabel, s.guest.email, (v) => (s.guest.email = v), "email");
    const phoneInput = textInput(this.m.phoneLabel, s.guest.phone, (v) => (s.guest.phone = v), "tel");

    const submit = el("button", { className: "bw-btn" }, [
      text(s.submitting ? this.m.addingWaitlistCta : this.m.addWaitlistCta),
    ]);
    (submit as HTMLButtonElement).disabled = s.submitting;
    submit.addEventListener("click", () => {
      if (!s.guest.fullName.trim() || (!s.guest.email && !s.guest.phone)) {
        s.error = this.m.nameAndContactRequired;
        this.render();
        return;
      }
      void this.submitWaitlist();
    });

    const back = el("button", { className: "bw-btn bw-btn-ghost" }, [text(this.m.back)]);
    back.addEventListener("click", () => {
      s.step = "pick";
      this.render();
    });

    return el("section", { className: "bw-body" }, [
      el("div", { className: "bw-summary" }, [
        text(interpolate(this.m.waitlistSummary, { date: s.date, n: s.partySize })),
      ]),
      nameInput,
      emailInput,
      phoneInput,
      s.error ? el("div", { className: "bw-error" }, [text(s.error)]) : el("span", {}, []),
      el("div", { className: "bw-actions" }, [back, submit]),
      this.privacyNotice(),
    ]);
  }

  private renderDone(): HTMLElement {
    const hold = this.state.hold;
    const heading = hold ? this.m.receivedTitle : this.m.waitlistTitle;
    const detail = hold
      ? interpolate(this.m.receivedSub, { ref: hold.referenceCode })
      : this.m.waitlistSub;
    return el("section", { className: "bw-body bw-body-done" }, [
      el("div", { className: "bw-done-title" }, [text(heading)]),
      el("div", { className: "bw-muted" }, [text(detail)]),
    ]);
  }
}

// ---------- DOM helpers ----------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | undefined> = {},
  children: Node[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === "className") node.className = String(v);
    else node.setAttribute(k, String(v));
  }
  for (const c of children) node.appendChild(c);
  return node;
}

function text(t: string): Text {
  return document.createTextNode(t);
}

function labelled(label: string, input: HTMLElement): HTMLElement {
  return el("label", { className: "bw-label" }, [el("span", {}, [text(label)]), input]);
}

function textInput(
  label: string,
  initial: string,
  onChange: (v: string) => void,
  type = "text",
): HTMLElement {
  const input = el("input", { type, className: "bw-input", value: initial });
  input.addEventListener("input", () => {
    onChange((input as HTMLInputElement).value);
  });
  return labelled(label, input);
}

function textareaInput(
  label: string,
  placeholder: string,
  initial: string,
  onChange: (v: string) => void,
): HTMLElement {
  const ta = el("textarea", { className: "bw-input bw-textarea", placeholder, rows: 2 }) as HTMLTextAreaElement;
  ta.value = initial;
  ta.addEventListener("input", () => { onChange(ta.value); });
  return labelled(label, ta);
}

// Layout CSS — appended after the theme tokens block. Uses CSS variables that
// the theme defines, so each instance can wear a different palette without
// rebuilding the layout rules.
const LAYOUT_CSS = /* css */ `
.bw-logo { max-width: 60px; max-height: 36px; object-fit: contain; margin-bottom: 0.45rem; }
.bw-header { display:flex; flex-direction:column; margin-bottom: 0.75rem; }
.bw-sub { color: var(--bw-muted, #666); font-size: 0.85rem; }
.bw-welcome { margin: 0.45rem 0 0; font-size: 0.88rem; color: var(--bw-muted, #555); }
.bw-body { display:grid; gap: 0.75rem; }
.bw-body-done { text-align: center; padding: 1rem 0; }
.bw-done-title { font-weight: 600; font-size: 1.05rem; margin-bottom: 0.25rem; }
.bw-row { display:grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; }
.bw-label { display:flex; flex-direction:column; gap: 0.25rem; font-size:0.85rem; color: var(--bw-text); }
.bw-input {
  font: inherit;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--bw-border, #d1d5db);
  border-radius: calc(var(--bw-radius, 12px) * 0.6);
  background: var(--bw-surface, #fff);
  color: var(--bw-text, #111);
  width: 100%;
  box-sizing: border-box;
}
.bw-textarea { min-height: 60px; resize: vertical; font-family: inherit; }
.bw-input:focus { outline: 2px solid var(--bw-accent, #3b82f6); outline-offset: 0; }
.bw-period { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bw-muted); margin-top: 0.5rem; }
.bw-slot-grid { display:grid; grid-template-columns: repeat(4, 1fr); gap: 0.4rem; }
.bw-slot {
  font: inherit;
  padding: 0.45rem 0;
  border: 1px solid var(--bw-border);
  border-radius: calc(var(--bw-radius) * 0.55);
  background: var(--bw-surface);
  color: var(--bw-text);
  cursor: pointer;
  font-size: 0.85rem;
}
.bw-slot:hover { border-color: var(--bw-accent); }
.bw-btn {
  font: inherit;
  padding: 0.55rem 0.9rem;
  background: var(--bw-accent, #111);
  color: var(--bw-accent-text, #fff);
  border: 1px solid var(--bw-accent, #111);
  border-radius: calc(var(--bw-radius) * 0.55);
  cursor: pointer;
  font-weight: 500;
}
.bw-btn:disabled { opacity: 0.6; cursor: default; }
.bw-btn-ghost {
  background: var(--bw-surface, #fff);
  color: var(--bw-text, #111);
  border-color: var(--bw-border, #d1d5db);
  font-weight: 400;
}
.bw-actions { display:flex; gap: 0.5rem; justify-content: flex-end; }
.bw-muted { color: var(--bw-muted); font-size: 0.9rem; }
.bw-summary {
  background: color-mix(in srgb, var(--bw-accent) 8%, var(--bw-surface));
  padding: 0.5rem 0.6rem; border-radius: calc(var(--bw-radius) * 0.55);
  font-size: 0.9rem;
}
.bw-error { color:#b00; font-size:0.85rem; }
.bw-privacy { margin: 0.75rem 0 0; font-size: 0.78rem; line-height: 1.45; color: var(--bw-muted, #666); }
.bw-privacy a { color: inherit; text-decoration: underline; }
.bw-loading, .bw-fatal {
  font-family: system-ui, sans-serif; padding: 1rem; color: var(--bw-muted, #666);
}
/* Allergies block */
.bw-allergy-block {
  display: grid; gap: 0.45rem;
  padding: 0.6rem 0.7rem;
  border: 1px solid var(--bw-border);
  border-radius: calc(var(--bw-radius) * 0.55);
  background: color-mix(in srgb, var(--bw-accent) 5%, var(--bw-surface));
}
.bw-allergy-title { font-size: 0.85rem; font-weight: 600; color: var(--bw-text); }
.bw-allergy-radios { display: flex; flex-direction: column; gap: 0.3rem; }
.bw-radio { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.88rem; color: var(--bw-text); cursor: pointer; }
.bw-radio input { accent-color: var(--bw-accent); }
.bw-consent { align-items: flex-start; font-size: 0.82rem; line-height: 1.4; min-height: 44px; }
.bw-consent input { margin-top: 0.15rem; flex-shrink: 0; }
`;
